import { getDiscountTotalWithoutShippingCoupon, getFilteredDiscountTotal } from '@cart-services/core/cartService'
import { floorToDecimalByCountry } from '@cart-services/core/rewardPointsService'
import { CountryCode } from '@constants'
import { DiscountItemType, type DiscountItem } from '@hooks-api/types/dataModel/coupon'
import { Box } from '@pets-ui/Box/Box'
import { Chip } from '@pets-ui/Chip/Chip'
import { Collapse } from '@pets-ui/Collapse/Collapse'
import { Divider } from '@pets-ui/Divider/Divider'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowDownSmall, ArrowUpSmall } from '@pets-ui/Icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { formatPrice } from '@utils'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DetailItem } from '../DetailItem/DetailItem'
import { useToggleSections } from '../hooks/use-order-detail-toggle-sections'
import cartUiI18nInstance from '../i18n/i18nConfig'

const styles = stylex.create({
  amountInfo: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  clickable: {
    cursor: 'pointer',
  },
  shippingFeeChip: {
    flexShrink: 1,
  },
})

interface OrderDetailProps {
  subtotal: number
  subtotalAfterDiscount: number
  addonV2Total: number
  shippingTotal: number
  total: number
  currency: string
  discountItems: DiscountItem[]
  rewardPoints?: number
  countryCode: CountryCode
  tax: number | null
  backgroundColor?: string
  expenseRate?: number
  appliedShippingFee?: number
  addonV2TotalBeforeDiscount: number
}

export const OrderDetail = (props: OrderDetailProps) => {
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })
  const {
    subtotal,
    shippingTotal,
    total,
    currency,
    discountItems,
    rewardPoints = 0,
    countryCode,
    tax,
    backgroundColor = colors.white,
    expenseRate,
    addonV2Total,
    appliedShippingFee,
    addonV2TotalBeforeDiscount,
  } = props

  const { isOpen, toggle } = useToggleSections()

  const singleCouponItems = useMemo(() =>
    discountItems
      ?.filter(item => !item.is_cart_level)
      .filter(item => item.type !== DiscountItemType.REWARD_POINT && item.type !== DiscountItemType.REFERRAL_CODE && item.type !== DiscountItemType.SHIPPING_COUPON && item.type !== DiscountItemType.ADDON_V2) || [],
  [discountItems])

  const multiCouponItems = useMemo(() => discountItems?.filter(item => item.is_cart_level && item.type !== DiscountItemType.ADDON_V2) || [], [discountItems])
  const shippingCouponItems = useMemo(() => discountItems?.filter(item => item.type === DiscountItemType.SHIPPING_COUPON) || [], [discountItems])
  const hasDiscount = singleCouponItems.length > 0 || multiCouponItems.length > 0
  const discountTotalWithoutShippingCoupon = getDiscountTotalWithoutShippingCoupon(discountItems || [])
  const filteredDiscountTotal = getFilteredDiscountTotal(discountItems || [])
  const isUs = countryCode === CountryCode.US
  const discountRewardPointsTotal = expenseRate !== undefined ? floorToDecimalByCountry(rewardPoints / expenseRate, countryCode) : 0
  const showRewardPoints = !!rewardPoints && discountRewardPointsTotal > 0
  return (
    <VStack padding={3} borderRadius={8} backgroundColor={backgroundColor}>
      <Box mb={3}>
        <Text variant="h5">{t('cartUi.transactionAmountSummary', { defaultValue: '交易金額摘要' })}</Text>
      </Box>
      <VStack borderRadius={2}>
        <Box rootStyle={styles.amountInfo} mb={3}>
          <Text>{t('cartUi.items', { defaultValue: '商品總計' })}</Text>
          <Text data-testid="text-subtotal-amount">
            {`$${subtotal.toLocaleString()}`}
          </Text>
        </Box>
        <Box
          rootStyle={[styles.amountInfo, hasDiscount && styles.clickable]}
          onClick={hasDiscount ? () => toggle('discount') : undefined}
          mb={3}
        >
          <HStack gap={1}>
            <Text>{t('cartUi.promotions', { defaultValue: '優惠折扣' })}</Text>
            {hasDiscount && (
              isOpen('discount')
                ? <ArrowUpSmall color={colors.grey600} fontSize={20} />
                : <ArrowDownSmall color={colors.grey600} fontSize={20} />
            )}
          </HStack>
          <Text data-testid="text-discount-amount">
            {`-$${filteredDiscountTotal.toLocaleString()}`}
          </Text>
        </Box>
        {hasDiscount
          && (
            <Collapse in={isOpen('discount')}>
              <VStack gap={1} p={2} backgroundColor={colors.grey50} borderRadius={4} mb={3}>
                {/* 單品 */}
                {singleCouponItems.length > 0
                  && singleCouponItems?.map((item, index) => {
                    return (
                      <DetailItem
                        key={index}
                        label={t('cartUi.productDeal', { defaultValue: '單品' })}
                        title={item.display}
                        discount={item.discount_amount}
                        currency={currency}
                        data-testid="text-discount-detail-amount"
                      />
                    )
                  })}
                {/* 全館 */}
                {multiCouponItems.length > 0
                  && multiCouponItems?.map((item, index) => {
                    return (
                      <DetailItem
                        key={index}
                        label={t('cartUi.allItems', { defaultValue: '全館' })}
                        title={item.display}
                        discount={item.discount_amount}
                        currency={currency}
                        data-testid="text-discount-detail-amount"
                      />
                    )
                  })}
              </VStack>
            </Collapse>
          )}
        {addonV2Total > 0 && addonV2TotalBeforeDiscount > 0 && (
          <Box rootStyle={styles.amountInfo} mb={3}>
            <Text>{t('cartUi.addonOffer', { defaultValue: '加價購' })}</Text>
            <HStack gap={1}>
              <Text data-testid="text-subtotal-before-discount" color={colors.grey500} lineThrough>
                {`$${addonV2TotalBeforeDiscount.toLocaleString()}`}
              </Text>
              <Text data-testid="text-subtotal-after-discount">
                {`$${addonV2Total.toLocaleString()}`}
              </Text>
            </HStack>
          </Box>
        )}
        <Box
          rootStyle={[styles.amountInfo, shippingCouponItems.length > 0 && styles.clickable]}
          onClick={shippingCouponItems.length > 0 ? () => toggle('shippingDiscount') : undefined}
          mb={3}
          gap={1}
        >
          <HStack gap={1} flex={1}>
            <Text noWrap>{t('cartUi.shippingFee', { defaultValue: '運費' })}</Text>
            {shippingCouponItems.length > 0 && (
              isOpen('shippingDiscount')
                ? <ArrowUpSmall color={colors.grey600} fontSize={20} />
                : <ArrowDownSmall color={colors.grey600} fontSize={20} />
            )}
          </HStack>
          {appliedShippingFee && appliedShippingFee > 0 && shippingTotal > 0 && (
            <Chip size="small" variant="square" color="red" rootStyle={styles.shippingFeeChip}>
              {t('cartUi.shippingConditionsGap', { appliedShippingFee: formatPrice(currency, appliedShippingFee), defaultValue: '差 {{appliedShippingFee}} 免運' })}
            </Chip>
          )}
          <Text data-testid="text-shipping-amount">
            {`$${shippingTotal.toLocaleString()}`}
          </Text>
        </Box>
        {shippingCouponItems.length > 0
          && (
            <Collapse in={isOpen('shippingDiscount')}>
              <VStack gap={1} p={2} backgroundColor={colors.grey50} borderRadius={4} mb={3}>
                {shippingCouponItems.map((item, index) => {
                  return (
                    <DetailItem
                      key={index}
                      label={t('cartUi.shippingFee', { defaultValue: '運費' })}
                      title={item.display}
                      discount={item.discount_amount}
                      currency={currency}
                      data-testid="text-shipping-discount-amount"
                    />
                  )
                })}
              </VStack>
            </Collapse>
          )}
        {showRewardPoints && (
          <Box rootStyle={styles.amountInfo} mb={3}>
            <Text>{t('cartUi.pointsOrReferral', { defaultValue: '點數/好友推薦' })}</Text>
            <Text data-testid="text-reward-points-amount">
              {`-$${discountRewardPointsTotal.toLocaleString()}`}
            </Text>
          </Box>
        )}
        {isUs && (
          <HStack gap={1} justifyContent="space-between" mb={3}>
            <Text>{t('cartUi.estimatedTax', { defaultValue: '稅額' })}</Text>
            <Text color={colors.grey800} data-testid="text-tax-amount">
              {tax === null ? '-' : `$${tax?.toLocaleString() ?? 0}`}
            </Text>
          </HStack>
        )}
        <Divider mb={3} />
        <Box rootStyle={styles.amountInfo}>
          <Text variant="h5">{t('cartUi.orderTotal', { defaultValue: '訂購總額' })}</Text>
          <HStack gap={2}>
            {discountTotalWithoutShippingCoupon > 0
              && (
                <Chip size="xSmall" isOutlined variant="square" color="green" data-testid="text-discount-total-amount">
                  {t('cartUi.savedAmount', { amount: formatPrice(currency, discountTotalWithoutShippingCoupon), defaultValue: '已省下 {{amount}}' })}
                </Chip>
              )}
            <Text variant="h5" data-testid="text-total-amount">
              {`$${total.toLocaleString()}`}
            </Text>
          </HStack>
        </Box>
      </VStack>
    </VStack>
  )
}
